import styles from './RiskPage.module.scss';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { Button } from '../../components';
import { Text } from '../../components/typography';
import SimpleUserCard from '../../components/SimpleUserCard';
import RiskMatrix from '../../components/RiskMatrix';
import { getLocalTime } from '../../utilities/time';
import Label from '../../components/action_items/Label';
import { calculateRisk } from '../../utilities/riskMatrix';
import { useModal } from '../../providers/ModalProvider';
import Notes from '../../components/notes/Notes';
import { useActionItemModal } from '../../hooks/misc';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import ActionItemCard from '../../components/action_items/ActionItemCard';
import Mitigations from './Mitigations';
import OpenClosedMenu from '../action_items/OpenClosedMenu';
import { useNavigate, useParams } from 'react-router-dom';
import Spacer from '../../components/typography/Spacer';
import SidebarLayout from '../../components/layouts/SidebarLayout';
import {
  GetRiskSidebarOptions,
  SidebarButtons,
  generateChangeLogString,
  handleRiskChange,
  handleRiskNavigate,
  getRecordTypeName,
} from './risk_page';
import DocumentTabSystem from '../../components/DocumentTabSystem';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import TextWithIcon from '../../components/typography/TextWithIcon';
import { useUserMutations } from '../../hooks/user/user_mutations';
import { useCurrentUser } from '../../providers/UserProvider';
import { showToast } from '../../graphql/cache/modal';
import InspectionCard from '../../components/inspections/inspection_items/InspectionCard';
import { getRoute, paths, assessmentStatus } from '../../constants/strings';
import { modals } from '../../providers/modals';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import { Icon, Tooltip } from '@mui/material';
import SimpleFileCard from '../../components/files/SimpleFileCard';
import { Form, Spinner } from 'react-bootstrap';
import Modal from '../../components/modals/Modal';
import { getLabel, incidentStatusTypes } from '../../utilities/incidents';
import { cpaStatusTypes } from '../../utilities/audits';
import classNames from 'classnames';
import { riskTypes } from '../../utilities/risk_registry';
import Select from 'react-select';

const updateRegistryMutation = loader('./RiskRegistryList.update.graphql');
const addActionItemMutation = loader(
  './RiskRegistryList.addActionItem.graphql',
);
const riskRegistryQuery = loader('./RiskPage.fetch.graphql');
const mitigationQuery = loader('./Mitigations.fetch.graphql');
const deleteRiskMutation = loader('./RiskPage.delete.graphql');
const actionItemQuery = loader('./RiskPage.actionItems.graphql');
const assessmentQuery = loader('./RiskPage.inspections.graphql');
const fileQuery = loader('./RiskPage.files.graphql');
const addFileMutation = loader('./RiskPage.addFile.graphql');
const removeMitigationMutation = loader('./RiskPage.removeMitigation.graphql');

const findRecordsToRelate = loader('./RiskPage.linkFetch.graphql');
const associateRecordToRiskMutation = loader(
  './RiskRegistrySelector.associate.graphql',
);
const deleteAssociatedRisk = loader('./RiskPage.deleteRisk.graphql');
const updateAssociatedRiskMutation = loader('./RiskPage.update.graphql');
const removeMitigationFromRiskMutation = loader(
  './RiskPage.removeMitFromRisk.graphql',
);
const removeRecordMutation = loader(
  './RiskRegistrySelector.disassociate.graphql',
);

const itemTypeLookup = {
  Incident: 'incident',
  CorrectiveAction: 'correctiveAction',
  Inspection: 'inspection',
};

function LinkModal({
  open = false,
  setOpen = () => {},
  refetch = () => {},
  riskRegistryId,
}) {
  const { t } = useTranslation();
  const { assessmentName } = useCurrentUser();
  const [linkRecordToRisk] = useMutation(associateRecordToRiskMutation);
  const [searchProps, setSearchProps] = useState({
    string: null,
    results: null,
    searching: false,
  });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useQuery(findRecordsToRelate, {
    skip: !searchProps.searching,
    variables: {
      searchTerm: `%${searchProps?.string}%`,
      riskRegistryId: riskRegistryId.toString(),
    },
    onCompleted: (res) => {
      const {
        incidents = [],
        correctiveActions = [],
        inspectionsByDocumentNumber = [],
        inspectionsBySubject = [],
      } = res;

      // Inspections have no externalId, so they're searched by documentNumber
      // and subject separately, then de-duped by id before merging in.
      const inspections = Array.from(
        new Map(
          [...inspectionsByDocumentNumber, ...inspectionsBySubject].map((i) => [
            i.id,
            i,
          ]),
        ).values(),
      );

      const notLinked = (record) =>
        !record?.risks?.map((r) => r?.id)?.includes(riskRegistryId);

      const allResults = [
        ...incidents.filter(notLinked),
        ...correctiveActions.filter(notLinked),
        ...inspections.filter(notLinked),
      ]
        ?.map((item) => {
          const isInspection = item.__typename === 'Inspection';
          const riskCalc = item.impact
            ? item.impact * item.probability
            : item.__typename === 'CorrectiveAction'
            ? item.riskLevel
            : isInspection
            ? item?.riskScore?.impact !== null
              ? item.riskScore?.impact * item.riskScore?.probability
              : null
            : null;

          const risk = calculateRisk(riskCalc);
          return {
            ...item,
            title:
              item?.title ??
              item?.externalId ??
              item?.subject ??
              item?.template?.title ??
              item?.documentNumber ??
              assessmentName ??
              'Assessment',
            risk: risk,
            itemType: itemTypeLookup[item.__typename],
            label: isInspection
              ? t(assessmentStatus[item.status]?.title) || item.status
              : getLabel(item.status),
            labelColor: isInspection
              ? assessmentStatus[item.status]?.color
              : statusTypes[item.status]?.color,
          };
        })
        ?.sort((a, b) =>
          a?.externalId && b?.externalId
            ? a.externalId - b.externalId
            : `${a?.externalId ?? a?.title ?? ''}`.localeCompare(
                `${b?.externalId ?? b?.title ?? ''}`,
              ),
        );

      setSearchProps((prev) => ({
        ...prev,
        results: allResults,
        searching: false,
      }));
    },
  });

  const onHide = () => {
    setOpen(false);
    setSearchProps({ string: null, searching: false, results: null });
    setSelected([]);
    setLoading(false);
    refetch();
  };

  const statusTypes = { ...incidentStatusTypes, ...cpaStatusTypes };

  const onSubmit = async () => {
    setLoading(true);
    for (const item of selected) {
      const [id, modelType] = item.split('_');
      const modelId = parseInt(id);

      await linkRecordToRisk({
        variables: { riskRegistryId, modelId, modelType },
      });
      setSelected((prev) => prev.filter((i) => i !== item));
    }
    setLoading(false);
    onHide();
  };

  return (
    <Modal
      open={open}
      title={'Link Record to Risk Registry Entry'}
      onClose={onHide}
      hideSubmit
      hideCancel
    >
      <div className={styles.linkButtonRow}>
        <Form.Control
          placeholder="Search by ID, Document Number, or Subject"
          onChange={(e) =>
            setSearchProps((prev) => ({ ...prev, string: e.target.value }))
          }
          onBlur={() =>
            setSearchProps((prev) => ({ ...prev, searching: true }))
          }
        />
        <Button value="Search" icon="search" onClick={() => {}} size="sm" />
      </div>
      <div className={styles.linkList}>
        {!!searchProps?.results ? (
          searchProps?.results?.length > 0 ? (
            searchProps?.results?.map(
              (
                { id, itemType, externalId, title, risk, label, labelColor },
                i,
              ) => (
                <div
                  className={classNames(
                    selected?.includes(`${id}_${itemType}`)
                      ? styles.linkCardSelected
                      : {},
                    styles.linkCard,
                    i % 2 !== 0 ? styles.linkOdd : {},
                  )}
                  onClick={() =>
                    setSelected((prev) =>
                      prev?.includes(`${id}_${itemType}`)
                        ? prev?.filter((i) => i !== `${id}_${itemType}`)
                        : [...prev, `${id}_${itemType}`],
                    )
                  }
                >
                  <Text noMargin weight="semiBold">
                    {title}
                  </Text>

                  <div className={styles.linkButtonRow}>
                    <Label
                      className={styles.label}
                      name={label}
                      color={labelColor}
                    />
                    <Label
                      name={risk?.level}
                      color={risk?.textColor}
                      rowSized
                    />
                    {title !== externalId && externalId && (
                      <Text noMargin weight="semiBold" color="secondary">
                        {externalId}
                      </Text>
                    )}
                  </div>
                </div>
              ),
            )
          ) : (
            <Text>None</Text>
          )
        ) : null}
      </div>
      {loading && <Spinner />}
      <div style={{ marginTop: '2rem' }} />
      <Button
        disabled={selected?.length < 1}
        onClick={async () => await onSubmit()}
        value={'Submit'}
        align="right"
      />
    </Modal>
  );
}

function RiskCard({ riskContent, refetch, riskRegistryId, workspaceId }) {
  const {
    residualRiskScore,
    impact: itemImpact,
    probability: itemProbability,
    riskScore,
    __typename,
    externalId,
    id,
    dateCreated,
    residualImpact,
    residualProbability,
    description,
    type,
    narrative,
    riskLevel,
    mitigations = [],
  } = riskContent;
  // Inspections have no impact/probability of their own — theirs comes from
  // the highest-scored item on the inspection (Inspection.riskScore).
  const impact = itemImpact ?? riskScore?.impact;
  const probability = itemProbability ?? riskScore?.probability;
  const editable = __typename === 'Risk';
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const { user, incidentName, assessmentName } = useCurrentUser();
  const isMilitary = user?.timeSetting === 'military';
  const displayType =
    type || getRecordTypeName({ __typename, incidentName, assessmentName });
  const { openConfirmationModal } = useModal();
  const [deleteRisk] = useMutation(deleteAssociatedRisk, {
    onCompleted: refetch,
  });
  const [updateRisk] = useMutation(updateAssociatedRiskMutation, {
    onCompleted: refetch,
  });
  const [removeMitigationFromRisk] = useMutation(
    removeMitigationFromRiskMutation,
  );
  const [removeRecord] = useMutation(removeRecordMutation);

  const [newState, setNewState] = useState({
    description,
    type,
    impact,
    probability,
    riskScore: null,
    mitigations,
  });
  const risk = calculateRisk(
    residualRiskScore ?? impact
      ? impact * probability
      : __typename === 'CorrectiveAction'
      ? riskLevel
      : null,
  );
  const onSubmit = async () => {
    const formattedMitigations = [
      ...(newState?.threats || []),
      ...(newState?.barriers || []),
      ...(newState?.consequences || []),
      ...(newState?.policies || []),
    ]?.map((m) => m?.value);
    await updateRisk({
      variables: {
        ...newState,
        mitigations: formattedMitigations,
        impact: parseInt(newState?.impact),
        probability: parseInt(newState?.probability),
        id,
      },
    });
    if (newState?.mitigationsToRemove?.length) {
      await removeMitigationFromRisk({
        variables: { riskId: id, mitigationIds: newState?.mitigationsToRemove },
      });
    }
  };
  return (
    <div className={styles.riskCard} onClick={() => editable && setOpen(id)}>
      <div className={styles.cardHeader}>
        {edit !== id && (
          <Label rowSized={false} name={risk?.level} color={risk?.textColor} />
        )}
        {!!externalId ? (
          <Text noMargin weight="semiBold" color="accentPrimary">
            {externalId}
          </Text>
        ) : edit !== id ? (
          <Text weight="semiBold" noMargin>
            {displayType}
          </Text>
        ) : (
          <div className={styles.editLink}>
            <Text marginXs weight="semiBold">
              Type
            </Text>
            <Select
              className={styles.linkSelect}
              options={Object.values(riskTypes)}
              onChange={(type) =>
                setNewState((prev) => ({ ...prev, type: type?.value }))
              }
              value={{ label: newState?.type, value: newState?.type }}
            />
          </div>
        )}
        <Text noMargin size="sm">
          {getLocalTime(dateCreated).format(
            isMilitary ? 'dddd MMMM Do YYYY HH:mm' : 'dddd MMMM Do YYYY h:mma',
          )}
        </Text>
        {!editable && (
          <div className={styles.riskCardButtons}>
            <Button
              size="sm"
              outlined
              icon="arrow_right"
              value={'Go'}
              onClick={() =>
                handleRiskNavigate({
                  navigate,
                  setOpen,
                  __typename,
                  id,
                  workspaceId,
                })
              }
            />
            <Button
              size="sm"
              outlined
              icon="delete"
              variant="danger"
              value={'Remove'}
              onClick={() =>
                openConfirmationModal({
                  title: `Remove Record`,
                  description: `Are you sure you want to remove this record from this risk. You can always add it back if needed.`,
                  variant: 'warning',
                  buttonText: 'Continue',
                  onSubmit: () =>
                    removeRecord({
                      variables: {
                        riskRegistryId,
                        modelId: id,
                        modelType: itemTypeLookup[__typename],
                      },
                    }).then(refetch),
                })
              }
            />
          </div>
        )}
        {open === id && (
          <div className={styles.riskCardButtons}>
            <Button
              size="sm"
              outlined
              icon="edit"
              variant={edit === id ? 'success' : 'primary'}
              value={edit === id ? 'Save' : 'Edit'}
              onClick={() => {
                if (id === edit) {
                  setEdit(null);
                  onSubmit();
                } else {
                  setEdit(id);
                }
              }}
            />
            <Button
              size="sm"
              outlined
              icon="delete"
              variant="danger"
              value={'Delete'}
              onClick={() =>
                openConfirmationModal({
                  title: `Delete Record`,
                  description: `Are you sure you want to delete this record?  This action cannot be undone.`,
                  variant: 'danger',
                  buttonText: 'Delete',
                  onSubmit: () =>
                    deleteRisk({ variables: { id } }).then(refetch),
                })
              }
            />
            <Icon
              className={styles.closeIcon}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(null);
              }}
            >
              close
            </Icon>
          </div>
        )}
      </div>
      {edit !== id ? (
        <div className={styles.flexRow}>
          <Text size="sm" noMargin weight="semiBold">{`Impact ${
            residualImpact ?? impact ?? 0
          }/5`}</Text>
          <Text size="sm" noMargin weight="semiBold">{`Probability ${
            residualProbability ?? probability ?? 0
          }/5`}</Text>
        </div>
      ) : (
        <div className={styles.marginBottomXs}>
          <RiskMatrix
            title={'Risk Assessment'}
            impact={parseInt(newState?.impact)}
            probability={parseInt(newState?.probability)}
            showMessage={false}
            onChange={(i, p) => {
              setNewState((prev) => ({
                ...prev,
                impact: parseInt(i),
                probability: parseInt(p),
              }));
            }}
            className={styles.matrixSm}
          />
        </div>
      )}
      {edit !== id ? (
        <div className={styles.header}>
          <Text truncate={open !== id} noMargin>
            {description ?? narrative ?? ''}
          </Text>
        </div>
      ) : (
        <div className={styles.marginBottomXs}>
          <Text marginXs weight="semiBold">
            Description
          </Text>
          <Form.Control
            as="textarea"
            value={newState?.description ?? null}
            onChange={(e) =>
              setNewState((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />
        </div>
      )}
      {open === id && (
        <div>
          <div className={styles.mitigations}>
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={newState}
              showProps={{ threats: true }}
              setState={setNewState}
              mitigations={mitigations || []}
              isEditing={edit === id}
            />
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={newState}
              showProps={{ consequences: true }}
              setState={setNewState}
              mitigations={mitigations || []}
              isEditing={edit === id}
            />
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={newState}
              showProps={{ barriers: true }}
              setState={setNewState}
              mitigations={mitigations || []}
              isEditing={edit === id}
            />
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={newState}
              showProps={{ policies: true }}
              setState={setNewState}
              mitigations={mitigations || []}
              isEditing={edit === id}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function RiskPage() {
  const { id } = useParams();
  const { user, incidentName, assessmentName } = useCurrentUser();
  const navigate = useNavigate();
  const [updateRegistry] = useMutation(updateRegistryMutation);
  const [isEditing, setIsEditing] = useState(false);
  const [addActionItem] = useMutation(addActionItemMutation);
  const [deleteRisk] = useMutation(deleteRiskMutation);
  const [removeMitigation] = useMutation(removeMitigationMutation);
  const [state, setState] = useState({});
  const [prevState, setPrevState] = useState({});
  const [selected, setSelected] = useState('Open');
  const [showChanges, setShowChanges] = useState(null);
  const [newDescription, setNewDescription] = useState(null);
  const [newCategory, setNewCategory] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const { openConfirmationModal, openModal } = useModal();
  const { openActionItemModal } = useActionItemModal();
  const { workspaceId, allWorkspaces } = useWorkspace();
  const { allUsers } = useWorkspacePermissions();
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const { addReviewer } = useUserMutations();
  const {
    data: {
      threats: fetchedThreats = [],
      barriers: fetchedBarriers = [],
      consequences: fetchedConsequences = [],
      policies: fetchedPolicies = [],
    } = {},
  } = useQuery(mitigationQuery);
  const fetchedMitigations = useMemo(
    () => [
      ...fetchedThreats,
      ...fetchedBarriers,
      ...fetchedConsequences,
      ...fetchedPolicies,
    ],
    [fetchedThreats, fetchedBarriers, fetchedConsequences, fetchedPolicies],
  );
  const { refetch } = useQuery(riskRegistryQuery, {
    skip: !id,
    variables: {
      options: {
        filters: [{ field: 'id', operator: 'eq', value: [String(id)] }],
      },
    },
    onCompleted: (res) => {
      if (!isEditing) {
        setState(res.riskRegistry[0] ?? null);
        setPrevState(res.riskRegistry[0] ?? null);
      }
    },
  });
  const { refetch: refetchActionItems, data: { actionItems = [] } = {} } =
    useQuery(actionItemQuery, {
      skip: !online || !id,
      variables: {
        options: {
          filters: [
            { field: 'riskRegistryId', operator: 'eq', value: [`${id}`] },
          ],
        },
      },
    });
  const { data: { inspections = [] } = {} } = useQuery(assessmentQuery, {
    skip: !online || !id,
    variables: {
      options: {
        filters: [
          { field: 'riskRegistryId', operator: 'eq', value: [`${id}`] },
        ],
      },
    },
  });
  const { refetch: refetchFiles, data: { files = [] } = {} } = useQuery(
    fileQuery,
    {
      skip: !online || !id,
      variables: {
        options: {
          filters: [
            { field: 'riskRegistryId', operator: 'eq', value: [`${id}`] },
          ],
        },
      },
    },
  );
  const [addFile] = useMutation(addFileMutation);

  const {
    description,
    category,
    impact,
    probability,
    mitigations = [],
    risks = [],
    incidents = [],
    correctiveActions = [],
    inspections: associatedInspections = [],
    residualImpact,
    residualProbability,
    reviewers = [],
    __typename,
  } = state || {};

  const mitigationCount = mitigations.reduce((counts, mitigation) => {
    if (!counts[mitigation.id]) {
      counts[mitigation.id] = { ...mitigation, count: 0 };
    }
    counts[mitigation.id].count += 1;
    return counts;
  }, {});

  const allMitigations = Object.values(mitigationCount);

  const { topFiveRisks, remainingRisks, recordCount } = useMemo(() => {
    // Inspections carry no externalId of their own, and their impact/
    // probability come from the highest-scored item on the inspection
    // (Inspection.riskScore)
    const normalizedInspections = (associatedInspections ?? []).map((i) => ({
      ...i,
      description:
        i?.subject || i?.template?.title || assessmentName || 'Assessment',
      impact: i?.riskScore?.impact ?? 0,
      probability: i?.riskScore?.probability ?? 0,
    }));

    const allRecords = [
      ...(risks ?? []),
      ...(incidents ?? []),
      ...(correctiveActions ?? []),
      ...normalizedInspections,
    ];

    const topFive =
      allRecords
        .sort((a, b) => b?.impact * b?.probability - a?.impact * a?.probability)
        .slice(0, 5) || [];

    const remainder =
      allRecords
        .sort((a, b) => b?.impact * b?.probability - a?.impact * a?.probability)
        .slice(5)
        .sort((a, b) => b?.dateCreated - a?.dateCreated) || [];

    return {
      topFiveRisks: topFive,
      remainingRisks: remainder,
      recordCount: topFive?.length + remainder?.length,
    };
  }, [
    risks,
    incidents,
    correctiveActions,
    associatedInspections,
    assessmentName,
  ]);

  const showActionItems = actionItems?.filter((a) =>
    selected === 'Open'
      ? a.status !== 'DONE' && a.status !== 'DUPLICATE'
      : a.status === 'DONE' || a.status === 'DUPLICATE',
  );

  const lookups = useMemo(
    () => ({
      users: allUsers,
      workspaces: allWorkspaces,
      mitigations: fetchedMitigations,
    }),
    [allUsers, allWorkspaces, fetchedMitigations],
  );

  const sidebarOptions = GetRiskSidebarOptions({
    state,
    setState,
    readOnly: !isEditing,
  });

  const buttons = SidebarButtons({
    readOnly: !isEditing,
    state,
    onCancel: () => {
      setIsEditing(false);
      setState(prevState);
      setNewCategory(null);
      setNewDescription(null);
    },
    onEdit: () => setIsEditing(true),
    onUpdate: () =>
      openConfirmationModal({
        textInput: true,
        variant: 'warning',
        title: 'Update Risk',
        description: `Updating this Risk Registry entry will only update your changes and will not update the status. Please enter any comments below.`,
        onSubmit: async (notes) => {
          const { changes: newValues, changeLog } = handleRiskChange(
            state,
            prevState,
          );

          if (state?.mitigationsToRemove?.length > 0) {
            await removeMitigation({
              variables: {
                riskId: parseInt(id),
                mitigationIds: state?.mitigationsToRemove,
              },
            });
          }

          await updateRegistry({
            variables: {
              id: parseInt(id),
              ...newValues,
              changeLog,
              notes,
            },
          });
          setIsEditing(false);
          setNewCategory(null);
          setNewDescription(null);
          refetch();
        },
      }),
    onReview: () =>
      openConfirmationModal({
        textInput: true,
        variant: 'warning',
        title: 'Review Risk',
        description: `Reviewing this Risk Registry entry will change the status to Reviewed and record you as the Reviewer. Please enter any comments below.`,
        onSubmit: async (notes) => {
          const { changes: newValues, changeLog } = handleRiskChange(
            state,
            prevState,
          );

          if (state?.mitigationsToRemove?.length > 0) {
            await removeMitigation({
              variables: {
                riskId: parseInt(id),
                mitigationIds: state?.mitigationsToRemove,
              },
            });
          }

          await updateRegistry({
            variables: {
              id: parseInt(id),
              ...newValues,
              changeLog,
              notes,
              status: 'REVIEWED',
            },
          });
          setIsEditing(false);
          setNewCategory(null);
          setNewDescription(null);
          refetch();
        },
      }),
    onDelete: () => {
      openConfirmationModal({
        title: 'Delete Risk',
        variant: 'danger',
        description:
          'Are you sure you want to delete this Risk? This action cannot be undone.',
        onSubmit: () =>
          deleteRisk({ variables: { id: parseInt(id) } }).then(() =>
            navigate('/registry'),
          ),
      });
    },
  });

  const riskSections = [
    {
      title: 'Details',
      key: 'DETAILS',
      component: (
        <div className={styles.component}>
          <div className={styles.centered}>
            <div>
              <RiskMatrix
                title={'Initial Risk Assessment'}
                impact={parseInt(impact)}
                probability={parseInt(probability)}
                showMessage={false}
                readOnly={!!risks?.length ? true : !isEditing}
                onChange={(i, p) => {
                  setState({
                    ...state,
                    impact: parseInt(i),
                    probability: parseInt(p),
                  });
                }}
                className={styles.matrix}
              />
            </div>
            <div>
              <RiskMatrix
                title="Mitigated Risk Assessment"
                className={
                  !state?.residualImpact && !residualImpact
                    ? styles.redBorder
                    : styles.matrix
                }
                impact={state?.residualImpact || residualImpact}
                probability={state?.residualProbability || residualProbability}
                showMessage={false}
                readOnly={!!risks?.length ? true : !isEditing}
                onChange={(i, p) => {
                  setState({
                    ...state,
                    residualImpact: parseInt(i),
                    residualProbability: parseInt(p),
                  });
                }}
              />
            </div>
          </div>
          <div className={styles.mitigations}>
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={state}
              showProps={{ threats: true }}
              setState={setState}
              mitigations={allMitigations || []}
              isEditing={isEditing}
            />
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={state}
              showProps={{ consequences: true }}
              setState={setState}
              mitigations={allMitigations || []}
              isEditing={isEditing}
            />
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={state}
              showProps={{ barriers: true }}
              setState={setState}
              mitigations={allMitigations || []}
              isEditing={isEditing}
            />
            <div className={styles.sectionLine} />
            <Mitigations
              className={styles.mitigationContainer}
              state={state}
              showProps={{ policies: true }}
              setState={setState}
              mitigations={allMitigations || []}
              isEditing={isEditing}
            />
          </div>
          <br />
        </div>
      ),
    },
    {
      title: 'Records',
      key: 'RISKS',
      component: (
        <div className={styles.component}>
          <div>
            <div className={styles.header}>
              <div className={styles.flexRow}>
                <TextWithIcon
                  leftIcon="rate_review"
                  weight="semiBold"
                  size="md-lg"
                  noMargin
                >
                  Records
                </TextWithIcon>
                <Tooltip
                  title={
                    <div>
                      <Text weight="semiBold" noMargin color="white">
                        This section is where you can see related records to
                        this entry. You can link existing records by clicking
                        the "Link" button. Existing records can either be{' '}
                        {incidentName || 'Incident'}s or CPAs. You can click the
                        "New" button to create a record without it being linked
                        to an {incidentName || 'Incident'} or CPA
                      </Text>
                    </div>
                  }
                  arrow
                >
                  <Icon
                    className={styles.secondary}
                    style={{ fontSize: '1rem' }}
                  >
                    help_outlined
                  </Icon>
                </Tooltip>
              </div>
              <div className={styles.buttonsContainer}>
                <Button
                  onClick={() => setLinkModalOpen(true)}
                  value={'Link'}
                  icon="link"
                  size="sm"
                  outlined
                />
                <Button
                  onClick={() =>
                    openModal({
                      modalName: modals.addRisk,
                      variables: { riskRegistryId: parseInt(id) },
                    })
                  }
                  value={'New'}
                  icon="add"
                  size="sm"
                />
              </div>
            </div>
          </div>

          {!!!recordCount ? (
            <Text noMargin color="yellow" weight="semiBold" textAlign="center">
              {t(keys.common.NONE)}
            </Text>
          ) : (
            <div className={styles.gapSmList}>
              <Text noMargin weight="semiBold">
                Top Five
              </Text>
              {topFiveRisks?.map((risk, i) => (
                <RiskCard
                  key={`top-5-${risk?.id}-${i}`}
                  riskContent={risk}
                  refetch={refetch}
                  riskRegistryId={parseInt(id)}
                  workspaceId={workspaceId}
                />
              ))}
              {remainingRisks?.length > 0 && (
                <>
                  <Text noMargin weight="semiBold">
                    Remaining Records
                  </Text>
                  {remainingRisks?.map((risk, i) => (
                    <RiskCard
                      key={`remaining-${risk?.id}-${i}`}
                      riskContent={risk}
                      refetch={refetch}
                      riskRegistryId={parseInt(id)}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Review',
      key: 'REVIEW',
      component: (
        <div className={styles.component}>
          <div>
            <div className={styles.header}>
              <div className={styles.flexRow}>
                <TextWithIcon
                  leftIcon="rate_review"
                  weight="semiBold"
                  size="md-lg"
                  noMargin
                >
                  Review History
                </TextWithIcon>
                <Tooltip
                  title={
                    <div>
                      <Text weight="semiBold" noMargin color="white">
                        This section is where you can see the history of this
                        entry. Row's with a "green" border are formal reviews of
                        this entry. The rest are just updates. You can click
                        "Details" to see what was changed
                      </Text>
                    </div>
                  }
                  arrow
                >
                  <Icon
                    className={styles.secondary}
                    style={{ fontSize: '1rem' }}
                  >
                    help_outlined
                  </Icon>
                </Tooltip>
              </div>
              <Button
                align={'right'}
                value="Review"
                size="sm"
                icon="edit"
                outlined
                onClick={() =>
                  openConfirmationModal({
                    textInput: true,
                    variant: 'warning',
                    title: 'Review',
                    description: 'Leave your review here',
                    onSubmit: async (notes) => {
                      await addReviewer({
                        userId: user.id,
                        notes,
                        reviewableId: parseInt(id),
                        reviewableType: 'riskRegistry',
                        status: 'REVIEWED',
                        onComplete: () => {
                          refetch();
                          showToast({
                            title: 'Review Submitted',
                            description: `Risk ${category} has successfully been reviewed!`,
                            variant: 'info',
                          });
                        },
                      });
                    },
                  })
                }
              />
            </div>
          </div>
          <Spacer size="xs" />
          <div className={styles.listContainer}>
            {[...(reviewers || [])]
              ?.sort((a, b) => b?.dateModified.localeCompare(a?.dateModified))
              ?.map(
                (
                  { id: revId, user, notes, dateModified, changeLog, status },
                  index,
                ) => (
                  <div
                    className={classNames(
                      styles.reviews,
                      status === 'REVIEWED'
                        ? styles.reviewBorder
                        : styles.updateBorder,
                      index % 2 !== 0 ? styles?.linkOdd : {},
                    )}
                    key={`reviewer-${index}`}
                  >
                    <div className={styles.flexRow}>
                      <div>
                        <SimpleUserCard mediumAvatar user={user} />
                      </div>
                      <Text noMargin size="sm">
                        {`${getLocalTime(dateModified).format(
                          'dddd MMMM DD YYYY HH:mm',
                        )}`}
                      </Text>
                    </div>
                    <Text className={styles.commentText} noMargin key={index}>
                      {notes || 'No Comment'}
                    </Text>
                    {Object.entries(changeLog ?? {})?.length > 0 ? (
                      <div>
                        {showChanges !== revId ? (
                          <Text
                            noMargin
                            color="accentPrimary"
                            hover
                            size="sm"
                            onClick={() =>
                              setShowChanges((prev) =>
                                prev === revId ? null : revId,
                              )
                            }
                          >
                            {t(keys.common.DETAILS)}
                          </Text>
                        ) : (
                          Object.entries(changeLog).map(
                            ([key, values], idx) => (
                              <div
                                key={`change-log-${idx}`}
                                className={styles.flexRow}
                                style={{ marginLeft: '2rem' }}
                                onClick={() =>
                                  setShowChanges((prev) =>
                                    prev === revId ? null : revId,
                                  )
                                }
                              >
                                <Icon style={{ fontSize: '0.5rem' }}>
                                  circle
                                </Icon>
                                <Text size="sm" noMargin>
                                  {generateChangeLogString(
                                    values,
                                    key,
                                    lookups,
                                  )}
                                </Text>
                              </div>
                            ),
                          )
                        )}
                      </div>
                    ) : (
                      <Text
                        color="secondary"
                        size="sm"
                        noMargin
                        onClick={() =>
                          setShowChanges((prev) =>
                            prev === revId ? null : revId,
                          )
                        }
                      >
                        No Changes
                      </Text>
                    )}
                  </div>
                ),
              )}
          </div>
        </div>
      ),
    },
    {
      title: 'Action Items',
      key: 'ACTIONS',
      component: (
        <div className={styles.component}>
          <div className={styles.header}>
            {actionItems?.length ? (
              <OpenClosedMenu
                options={['Open', 'Closed']}
                selected={selected}
                setSelected={setSelected}
              />
            ) : null}
            <Button
              align={'right'}
              size="sm"
              value={t(keys.common.NEW)}
              icon="add"
              onClick={() => {
                addActionItem({
                  variables: {
                    riskRegistryId: parseInt(id),
                    title: `${category}`,
                    description,
                    workspaceId,
                  },
                }).then(
                  ({
                    data: {
                      addActionItem: { id },
                    },
                  }) => {
                    openActionItemModal(id, 'REGISTRY');
                    refetchActionItems();
                  },
                );
              }}
            />
          </div>
          {!!showActionItems?.length ? (
            <div className={styles.listContainer}>
              {showActionItems?.map((a) => (
                <ActionItemCard
                  showName={true}
                  boxShadow={false}
                  key={a.id}
                  actionItem={a}
                  className={styles.actionItemCard}
                  onClick={() => openActionItemModal(a.id, 'REGISTRY')}
                />
              ))}
            </div>
          ) : (
            <div>
              <br />
              <Text
                noMargin
                weight="semiBold"
                textAlign="center"
                color="yellow"
              >
                {t(keys.action_items.NOT_FOUND)}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Reports',
      key: 'REPORTS',
      component: (
        <div className={styles.component}>
          <div className={styles.header}>
            <Button
              align={'right'}
              size="sm"
              value={t(keys.common.NEW)}
              icon="add"
              onClick={() => {
                openModal({
                  modalName: modals.createAssessment,
                  variables: { riskRegistryId: parseInt(id) },
                });
              }}
            />
          </div>
          {!!inspections?.length ? (
            <div className={styles.listContainer}>
              {inspections?.map((i) => (
                <InspectionCard
                  key={i.id}
                  inspection={i}
                  onClick={() => {
                    navigate(getRoute(workspaceId, paths.assessment, i?.id));
                  }}
                  className={styles.inspectionCard}
                />
              ))}
            </div>
          ) : (
            <div>
              <br />
              <Text
                noMargin
                weight="semiBold"
                textAlign="center"
                color="yellow"
              >
                {t(keys.common.NONE)}
              </Text>
            </div>
          )}
        </div>
      ),
      count: inspections?.length,
    },
    {
      title: 'Files',
      key: 'FILES',
      component: (
        <div className={styles.component}>
          <div className={styles.header}>
            <Button
              align={'right'}
              size="sm"
              value={t(keys.common.NEW)}
              icon="add"
              onClick={() => {
                openModal({
                  modalName: modals.fileUpload,
                  variables: {
                    riskRegistryId: parseInt(id),
                    simple: true,
                    onSubmit: (file) =>
                      addFile({
                        variables: { ...file },
                        onCompleted: refetchFiles,
                      }),
                  },
                });
              }}
            />
          </div>
          {!!files?.length ? (
            <div className={styles.wrapListContainer}>
              {files?.map((f) => (
                <SimpleFileCard
                  key={f.id}
                  extension={f?.extension}
                  file={f}
                  className={styles.fileCard}
                  readOnly={false}
                  fileName={f?.name}
                />
              ))}
            </div>
          ) : (
            <div>
              <br />
              <Text
                noMargin
                weight="semiBold"
                textAlign="center"
                color="yellow"
              >
                {t(keys.common.NONE)}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Notes',
      key: 'NOTES',
      component: (
        <div className={styles.component}>
          <Notes parentId={parseInt(id)} type={__typename} />
        </div>
      ),
    },
  ];

  return !state?.id ? null : (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.left}>
          <div>
            {isEditing ? (
              <>
                <Form.Control
                  value={newCategory ?? category}
                  onChange={(e) => {
                    setNewCategory(e?.target?.value);
                    setState((prev) => ({
                      ...prev,
                      category: e?.target?.value,
                    }));
                  }}
                />
                <div className={styles.spacer} />
              </>
            ) : (
              <Text noMargin size="lg" weight="semiBold">
                <span className={styles.bold}>Risk: </span>
                {category}
              </Text>
            )}
            {isEditing ? (
              <Form.Control
                as="textarea"
                rows={4}
                value={newDescription ?? description}
                onChange={(e) => {
                  setNewDescription(e?.target?.value);
                  setState((prev) => ({
                    ...prev,
                    description: e?.target?.value,
                  }));
                }}
              />
            ) : (
              <Text noMargin>{description}</Text>
            )}
          </div>
          <Spacer />
          <DocumentTabSystem options={riskSections} />
        </div>
        <div className={styles.right}>
          <SidebarLayout
            options={sidebarOptions}
            buttons={buttons}
            readOnly={!isEditing}
          />
        </div>
      </div>
      <LinkModal
        open={linkModalOpen}
        setOpen={setLinkModalOpen}
        riskRegistryId={parseInt(id)}
        refetch={refetch}
      />
    </div>
  );
}
